import type { ListOccasion } from "@listou/shared/constants";
import { stickerUrl } from "@listou/shared/stickers";

/**
 * The four occasion landings: wording, look and SEO in one place, so a route file only
 * names its occasion. The routes' head() reads the SEO fields, on the server and in the
 * browser alike.
 */
export type LandingOccasion = Exclude<ListOccasion, "other">;

export type OccasionPage = {
  occasion: LandingOccasion;
  /** The birth list lives on the home: two pages on one subject split its ranking. */
  path: "/" | "/liste-de-noel" | "/liste-anniversaire" | "/liste-de-mariage";
  /** Short name for links: "Liste de naissance". */
  linkLabel: string;
  /** "Créer ma liste de naissance". */
  cta: string;
  h1: string;
  subtitle: string;
  features: { title: string; text: string; sticker: string }[];
  faq: [question: string, answer: string][];
  seoTitle: string;
  seoDescription: string;
  /** 1200x630 link-preview card, made by `apps/server/scripts/make-og-images.ts`. */
  ogImage: string;
  /** Stickers stuck around the hero mockup. */
  heroStickers: string[];
  /** Soft band colour behind the features. */
  band: string;
};

const s = stickerUrl;
const NO_ACCOUNT: [string, string] = [
  "Mes proches doivent-ils créer un compte ?",
  "Non, le lien suffit.",
];

export const OCCASION_PAGES: Record<LandingOccasion, OccasionPage> = {
  birth: {
    occasion: "birth",
    path: "/",
    linkLabel: "Liste de naissance",
    cta: "Créer ma liste de naissance",
    h1: "La liste de naissance gratuite, sans cadeau en double.",
    subtitle:
      "Ajoutez des articles de toutes les boutiques, envoyez le lien, et arrêtez de répondre « qu'est-ce qui vous ferait plaisir ? ». La famille réserve en un clic, chacun voit ce qui est déjà pris, et personne n'achète le même doudou.",
    features: [
      {
        title: "Sur le faire-part",
        text: "Imprimez le QR code de votre liste sur le faire-part : il suffit de le scanner pour l'ouvrir. Des visuels prêts pour Instagram aussi.",
        sticker: s("bebe", "cigogne"),
      },
      {
        title: "Ce qu'on oublie toujours",
        text: "Les tailles 3, 6 et 12 mois, le thermomètre de bain, la veilleuse : l'assistant vous le rappelle.",
        sticker: s("bebe", "body"),
      },
      {
        title: "En plusieurs exemplaires",
        text: "Trois bodies, deux turbulettes : indiquez une quantité, chacun en réserve une partie.",
        sticker: s("bebe", "chaussons"),
      },
      {
        title: "Le prénom plus tard",
        text: "Vous ne le connaissez pas encore, ou vous le gardez secret ? Ajoutez-le quand vous voulez.",
        sticker: s("bebe", "lune"),
      },
    ],
    faq: [
      [
        "La liste de naissance Listou est-elle vraiment gratuite ?",
        "Oui, pour vous comme pour vos proches : pas d'abonnement, pas de frais, pas de limite d'articles ni de réservations. Listou se rémunère par l'affiliation : quand un proche achète via certains liens, la boutique nous reverse une petite commission, sans changer le prix.",
      ],
      [
        "Peut-on faire une liste de naissance multi-boutiques ?",
        "Oui. Collez le lien d'un article de n'importe quelle boutique (Vertbaudet, Aubert, Amazon, Orchestra, un créateur Etsy…) : Listou récupère la photo et le prix. Si une boutique ne se laisse pas lire, vous complétez à la main.",
      ],
      [
        "Quand créer sa liste de naissance ?",
        "Souvent vers le 6e mois de grossesse, pour avoir le temps de la partager avant la naissance. Mais il n'y a pas de mauvais moment.",
      ],
      [
        "Mes proches doivent-ils créer un compte ?",
        "Non. Le lien suffit : ils ouvrent la liste, choisissent un cadeau et le réservent en un clic.",
      ],
      [
        "Et si deux personnes veulent offrir le même cadeau ?",
        "Impossible : dès qu'un cadeau est réservé, il l'est pour tout le monde. Pour ce qui s'offre en plusieurs exemplaires, indiquez une quantité.",
      ],
      [
        "Comment partager sa liste de naissance ?",
        "Par WhatsApp, SMS ou e-mail, ou avec le QR code imprimé sur le faire-part. Le lien a trop circulé ? Générez-en un nouveau, l'ancien cesse de fonctionner.",
      ],
      [
        "Peut-on recevoir des participations en argent ?",
        "Ajoutez le lien de votre cagnotte (Leetchi, Lydia…) : un bloc « Participer librement » apparaît en bas de la liste. Listou ne fait transiter aucun argent.",
      ],
      [
        "Comment retrouver la liste de naissance de quelqu'un ?",
        "Les listes Listou ne sont pas publiées dans un annuaire : seuls ceux qui ont le lien peuvent les ouvrir. Demandez-le aux parents, il figure souvent sur le faire-part en QR code.",
      ],
      [
        "Quelqu'un a réservé puis changé d'avis ?",
        "Il peut annuler depuis le lien reçu par e-mail. Le cadeau redevient disponible.",
      ],
    ],
    seoTitle: "Liste de naissance gratuite et multi-boutiques, sans doublons · Listou",
    seoDescription:
      "Créez votre liste de naissance en ligne en deux minutes, avec des articles de toutes les boutiques. Vos proches réservent en un clic, sans compte, et la surprise reste gardée. 100 % gratuit.",
    ogImage: "/og/naissance.png",
    heroStickers: [
      s("bebe", "biberon"),
      s("bebe", "lune"),
      s("bebe", "chaussons"),
      s("bebe", "hochet"),
    ],
    band: "#eef4fa",
  },
  christmas: {
    occasion: "christmas",
    path: "/liste-de-noel",
    linkLabel: "Liste de Noël",
    cta: "Créer ma liste de Noël",
    h1: "Une seule liste pour toute la famille, du sapin à la hotte.",
    subtitle:
      "Les enfants, les parents, les grands-parents : chacun sa liste, et un seul lien à envoyer. On réserve en un clic, et personne ne découvre le 24 au soir qu'il a acheté le même Lego que Tonton.",
    features: [
      {
        title: "Une liste par enfant",
        text: "Une liste pour Léo, une pour Jade, chacune à son image.",
        sticker: s("noel", "chaussette"),
      },
      {
        title: "Le Père Noël garde le secret",
        text: "Vous ne voyez pas ce qui a été réservé. La magie reste entière.",
        sticker: s("noel", "bonnet"),
      },
      {
        title: "Un rappel avant les fêtes",
        text: "On vous prévient trois semaines avant, pour avoir le temps de renvoyer le lien.",
        sticker: s("noel", "renne"),
      },
      {
        title: "Mamie n'installe rien",
        text: "Elle ouvre le lien, elle choisit, c'est fait.",
        sticker: s("hiver", "mug"),
      },
    ],
    faq: [
      [
        "Quand envoyer sa liste de Noël ?",
        "Dès la mi-novembre : c'est là que la famille commence à chercher.",
      ],
      [
        "Les enfants peuvent-ils faire leur liste eux-mêmes ?",
        "Faites-la ensemble : ils choisissent leurs stickers, vous collez les liens.",
      ],
      [
        "Peut-on réutiliser la liste l'an prochain ?",
        "Archivez-la après les fêtes et créez-en une nouvelle en deux minutes.",
      ],
      NO_ACCOUNT,
    ],
    seoTitle: "Liste de Noël en famille, gratuite · Listou",
    seoDescription:
      "Une liste de Noël par enfant, un seul lien pour toute la famille. Chacun réserve son cadeau, personne n'offre deux fois la même chose.",
    ogImage: "/og/noel.png",
    heroStickers: [
      s("noel", "sapin"),
      s("noel", "bonhomme"),
      s("noel", "cadeau"),
      s("noel", "sucre-orge"),
    ],
    band: "#f1efe4",
  },
  birthday: {
    occasion: "birthday",
    path: "/liste-anniversaire",
    linkLabel: "Liste d'anniversaire",
    cta: "Créer ma liste d'anniversaire",
    h1: "Des cadeaux qui leur ressemblent, sans les doublons.",
    subtitle:
      "Pour les 6 ans de Léo ou les 40 ans de Sarah : une liste à glisser dans l'invitation, et chaque invité sait quoi offrir. Fini les trois boîtes de Playmobil identiques.",
    features: [
      {
        title: "Dans l'invitation",
        text: "Un lien ou un QR code, et les parents des copains savent quoi offrir.",
        sticker: s("fete", "ballons"),
      },
      {
        title: "À leur image",
        text: "Dinosaures, licornes, espace ou pirates : 18 univers illustrés pour habiller la page.",
        sticker: s("dinos", "trex"),
      },
      {
        title: "Des idées par âge",
        text: "En panne d'inspiration ? L'assistant propose des idées adaptées.",
        sticker: s("espace", "fusee"),
      },
      {
        title: "Pour les grands aussi",
        text: "Un anniversaire rond, un départ, une crémaillère : même simplicité.",
        sticker: s("fete", "gateau"),
      },
    ],
    faq: [
      [
        "Comment partager la liste avec les invités ?",
        "Par WhatsApp, SMS ou e-mail, ou en QR code sur le carton d'invitation.",
      ],
      [
        "Un cadeau peut-il être offert à plusieurs ?",
        "Indiquez une quantité : plusieurs invités peuvent offrir le même article.",
      ],
      NO_ACCOUNT,
    ],
    seoTitle: "Liste d'anniversaire pour enfants et adultes · Listou",
    seoDescription:
      "Une liste d'anniversaire à glisser dans l'invitation. Les invités réservent en un clic, sans doublons.",
    ogImage: "/og/anniversaire.png",
    heroStickers: [
      s("dinos", "tricera"),
      s("fete", "chapeau"),
      s("dinos", "diplo"),
      s("fete", "confettis"),
    ],
    band: "#fff4d6",
  },
  wedding: {
    occasion: "wedding",
    path: "/liste-de-mariage",
    linkLabel: "Liste de mariage",
    cta: "Créer ma liste de mariage",
    h1: "Votre liste de mariage, élégante et sans prise de tête.",
    subtitle:
      "Le service à thé, le robot pâtissier ou une nuit à Lisbonne pour le voyage de noces : réunissez vos envies sur une page qui vous ressemble, et partagez-la sur le faire-part.",
    features: [
      {
        title: "De toutes les boutiques",
        text: "Pas enfermés dans un seul magasin : chaque envie vient d'où vous voulez.",
        sticker: s("mariage", "bouquet"),
      },
      {
        title: "Une page à votre image",
        text: "Un modèle faire-part élégant, vos couleurs, votre photo.",
        sticker: s("mariage", "enveloppe"),
      },
      {
        title: "À deux",
        text: "Vous gérez la liste ensemble, depuis vos deux comptes.",
        sticker: s("mariage", "alliances"),
      },
      {
        title: "Le voyage de noces aussi",
        text: "Ajoutez des envies sans lien : une nuit, un dîner, une activité.",
        sticker: s("mariage", "voiture"),
      },
    ],
    faq: [
      [
        "Quand partager sa liste de mariage ?",
        "Avec le faire-part, ou deux à trois mois avant le jour J.",
      ],
      [
        "Peut-on mettre des expériences ?",
        "Oui : une envie n'a pas besoin de lien. Décrivez-la, indiquez un prix.",
      ],
      ["Mes invités doivent-ils créer un compte ?", "Non, le lien suffit."],
    ],
    seoTitle: "Liste de mariage de toutes les boutiques · Listou",
    seoDescription:
      "Une liste de mariage élégante, avec des envies de toutes les boutiques et des expériences. À partager sur le faire-part.",
    ogImage: "/og/mariage.png",
    heroStickers: [
      s("mariage", "champagne"),
      s("mariage", "pivoine"),
      s("mariage", "colombes"),
      s("mariage", "gateau"),
    ],
    band: "#f6f1e9",
  },
};

/** Order in menus and footers. */
export const OCCASION_PAGE_ORDER: LandingOccasion[] = ["birth", "christmas", "birthday", "wedding"];
